import SearchableInput from "../../pages/Signup/components/searchableInput";
import { useFormikContext } from "formik";
import {
  useGetAllCountriesQuery,
  useGetAllStatesQuery,
  useGetAllCitiesQuery,
} from "../../service/locationApi";
import { useState, useMemo, useCallback } from "react";

interface AddressSectionProps {
  field?: string;
}

interface LocationOption {
  value: string;
  label: string;
  code?: string;
  _id?: string;
}

interface CountryType {
  name: string;
  code: string;
  _id: string;
}

interface StateType {
  name: string;
  code: string;
  country: CountryType;
  _id: string;
}

interface CityType {
  name: string;
  _id: string;
  state: StateType;
  country: CountryType;
}

const AddressSection = ({ field = "address" }: AddressSectionProps) => {
  const { values, setFieldValue } = useFormikContext<any>();

  // State for client-side search
  const [countrySearch, setCountrySearch] = useState("");
  const [stateSearch, setStateSearch] = useState("");
  const [citySearch, setCitySearch] = useState("");

  // Access the address object using the dynamic field prop
  const addressData =
    field.split(".").reduce((obj, key) => obj?.[key], values) || {};

  // Get country data from API - fetch all at once
  const { data: countriesData, isLoading: countriesLoading } =
    useGetAllCountriesQuery({
      page: 1,
      limit: 10000, // High limit to get all countries at once
    });

  // Get state data from API (requires countryId)
  const selectedCountryId = useMemo(() => {
    if (!addressData.country || !countriesData?.data?.countries) return null;
    const found = countriesData.data.countries.find(
      (c: CountryType) =>
        c.name.toLowerCase() === addressData.country.toLowerCase()
    );
    return found?._id || null;
  }, [addressData.country, countriesData?.data?.countries]);

  const {
    data: statesData,
    isLoading: statesLoading,
    isFetching: statesFetching,
  } = useGetAllStatesQuery(
    {
      country: selectedCountryId || "",
      page: 1,
      limit: 10000, // High limit to get all states at once
    },
    {
      skip: !selectedCountryId, // Skip query if no country selected
    }
  );

  // Get city data from API (requires stateId and countryId)
  const selectedStateId = useMemo(() => {
    if (!addressData.state || !statesData?.data?.states) return null;
    const found = statesData.data.states.find(
      (s: StateType) => s.name.toLowerCase() === addressData.state.toLowerCase()
    );
    return found?._id || null;
  }, [addressData.state, statesData?.data?.states]);

  const {
    data: citiesData,
    isLoading: citiesLoading,
    isFetching: citiesFetching,
  } = useGetAllCitiesQuery(
    {
      state: selectedStateId || "",
      country: selectedCountryId || "",
      page: 1,
      limit: 10000, // High limit to get all cities at once
    },
    {
      skip: !selectedStateId || !selectedCountryId, // Skip query if no state or country selected
    }
  );

  // Transform countries data to options
  const countryOptions: LocationOption[] = useMemo(
    () =>
      countriesData?.data?.countries?.map((country: CountryType) => ({
        label: country.name,
        value: country.name,
        _id: country._id,
      })) || [],
    [countriesData?.data?.countries]
  );

  // Transform states data to options
  const stateOptions: LocationOption[] = useMemo(
    () =>
      statesData?.data?.states?.map((state: StateType) => ({
        label: state.name,
        value: state.name,
        _id: state._id,
      })) || [],
    [statesData?.data?.states]
  );

  // Transform cities data to options
  const cityOptions: LocationOption[] = useMemo(
    () =>
      citiesData?.data?.cities?.map((city: CityType) => ({
        label: city.name,
        value: city.name,
        _id: city._id,
      })) || [],
    [citiesData?.data?.cities]
  );

  const handleCountrySearch = useCallback((value: string) => {
    setCountrySearch(value);
  }, []);

  const handleStateSearch = useCallback((value: string) => {
    setStateSearch(value);
  }, []);

  const handleCitySearch = useCallback((value: string) => {
    setCitySearch(value);
  }, []);

  const handleCountryChange = useCallback(
    (e: any) => {
      setFieldValue(`${field}.country`, e.target.value);
      // Clear state and city when country changes
      setFieldValue(`${field}.state`, "");
      setFieldValue(`${field}.city`, "");
    },
    [field, setFieldValue]
  );

  const handleStateChange = useCallback(
    (e: any) => {
      setFieldValue(`${field}.state`, e.target.value);
      // Clear city when state changes
      setFieldValue(`${field}.city`, "");
    },
    [field, setFieldValue]
  );

  return (
    <>
      <div className=" col-span-2">
        <h3 className="text-lg font-semibold -mb-3">Address Details</h3>
      </div>
      <div className="fieldset flex flex-col">
        <SearchableInput
          options={countryOptions}
          name={`${field}.country`}
          label="Country"
          id={`${field}.country`}
          required
          isLoading={countriesLoading}
          onSearch={handleCountrySearch}
          searchValue={countrySearch}
          onChange={handleCountryChange}
        />
      </div>
      <div className="fieldset flex flex-col">
        <SearchableInput
          id={`${field}.state`}
          name={`${field}.state`}
          label="State"
          options={stateOptions}
          required
          isLoading={statesLoading || statesFetching}
          disabled={!addressData.country}
          onSearch={handleStateSearch}
          searchValue={stateSearch}
          onChange={handleStateChange}
          disabledMessage={
            !addressData.country ? "Select a country first" : undefined
          }
        />
      </div>
      <div className="fieldset flex flex-col">
        <SearchableInput
          id={`${field}.city`}
          name={`${field}.city`}
          label="City"
          options={cityOptions}
          required
          isLoading={citiesLoading || citiesFetching}
          disabled={!addressData.state || !addressData.country}
          onSearch={handleCitySearch}
          searchValue={citySearch}
          disabledMessage={
            !addressData.country || !addressData.state
              ? "Select country and state first"
              : undefined
          }
        />
      </div>
    </>
  );
};

export default AddressSection;
